import { Component, ComponentChild, h } from 'preact'
import Header from '../header'
import EmptyPage from '../empty-page/EmptyPage'
import globals from '../../utils/globals'
import ButtonControl from '../button-control'
import Table from '../table/Table'
import { PaginationProps } from '../ui-components/PaginationComponent'
import axios from 'axios'
import Loader from '../ui-components/Loader'
import AlertComponent from '../ui-components/AlertComponent'

interface Row {
    header?: string | ComponentChild,
    accessor: string,
    cell: string | ComponentChild,
    colspan?: number
}
interface Company {
    name: string,
    emailDomain: string,
    status: string
}

interface Props {
  redirectMessage?: string,
  companies?: Array<Company>,
  total?: number
}

interface State {
    companies: Array<Company>,
    pagination: PaginationProps,
    isPaginationSet: boolean,
    isLoading: boolean,

    messageType?: string|null,
    message?: string
}

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 25

const DEFAULT_PAGINATION = {
  pagination: {
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE
  }
}
const PAGINATION_STORAGE_KEY = 'companyPagination'

class CompanyOverview extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.setState({
      companies: this.props.companies ?? [],
      isPaginationSet: false,
      isLoading: true,
      pagination: {
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        total: this.props.total ?? undefined
      },
      message: this.props.redirectMessage ?? undefined,
      messageType: this.props.redirectMessage ? 'success' : undefined
    })
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
  }

  async componentDidMount () {
    window.history.pushState(null, '', window.location.href.split('?')[0])
    if (!window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)) {
      window.sessionStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify(DEFAULT_PAGINATION))
    } else {
      let item = window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)
      const storedPagination = item ? JSON.parse(item) : null
      const { pagination: { pageSize, page } } = storedPagination
      const isPaginationSet = JSON.stringify(storedPagination) !== JSON.stringify(DEFAULT_PAGINATION)
      const newPagination = this.state.pagination
      newPagination.pageSize = pageSize
      newPagination.page = page
      this.setState({ pagination: newPagination, isPaginationSet: isPaginationSet })
    }
    await this.fetchCompanies(this.state.pagination.page ?? DEFAULT_PAGE,this.state.pagination.pageSize ?? DEFAULT_PAGE_SIZE)
  }

  async fetchCompanies (page: number, pageSize: number | undefined) {
    this.setState({ isLoading: true })
    const url = `/company/fetch?page=${page}&pageSize=${pageSize}`
    try {
      const res = await axios.get(url)
      const { companies, total } = res.data
      const newPagination = this.state.pagination
      newPagination.total = total
      this.setState({ companies: companies, pagination: newPagination, isLoading: false })
    } catch {
      this.setState({
        isLoading: false,
        message: 'Unable to fetch companies. Please try again later.',
        messageType: 'error'
      })
    }
  }

  onPageChange (page: number) {
    const pageSize = this.state.pagination.pageSize
    this.updatePagination(page, pageSize)
    this.fetchCompanies(page, pageSize)
  }

  onPageSizeChange (pageSize: number) {
    const page = DEFAULT_PAGE
    this.updatePagination(page, pageSize)
    this.fetchCompanies(page, pageSize)
  }

  updatePagination (page: number, pageSize: number | undefined) {
    const newPagination = this.state.pagination
    newPagination.page = page
    newPagination.pageSize = pageSize
    this.setState({ pagination: newPagination })
    this.saveSessionPagination(page, pageSize)
  }

  saveSessionPagination (page: number, pageSize: number | undefined) {
    let item = window.sessionStorage.getItem(PAGINATION_STORAGE_KEY)
    let storage = item ? JSON.parse(item) : null
    storage['pagination'] = { page, pageSize }
    window.sessionStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify(storage))
  }

  getPaginationOptions () {
    const { pagination } = this.state
    if (pagination === undefined) {
      return undefined
    }
    const { total } = pagination
    if (total !== undefined && total > 25) {
      return {
        pagination: Object.assign({}, pagination, { total }),
        onPageChange: this.onPageChange,
        onPageSizeChange: this.onPageSizeChange
      }
    }
    return undefined
  }

  getTableRows (companies: Array<Company>) {
    return companies.map((company: Company): Array<Row> => [{
      header: 'Name',
      accessor: 'company-name',
      cell: <span >{company.name}</span>
    }, {
      header: 'Email domain',
      accessor: 'company-email-domain',
      cell: <span >{company.emailDomain}</span>
    }, {
      header: 'Status',
      accessor: 'company-status',
      cell: <span>{company.status}</span>
    }
    ])
  }

  render (_: Props, { companies, pagination, isLoading, messageType, message }: State) {
    const defaultCdn = globals('defaultCdn')
    const isVisibleFor = messageType === 'success' ? 5000 : false

    return (
      <div className='company-overview qa-company-overview'>
        <Header
          title='Company Overview'
          disableCancelButton
          disableSaveButton
          customButtons={[<ImportCompanyButton />]}
        />
        <div className='company-overview-container'>
          {isLoading && <Loader />}
          {
            messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
          }
          {companies.length > 0 ? <div>
            <Table rows={this.getTableRows(companies)} rowId={'name'} classes={['company__table']} withPagination={this.getPaginationOptions()} />
          </div>
            : <EmptyPage
              img_src={`${defaultCdn}/images/empty_page.png`}
              header='No company found'
              text={`You haven't added any companies yet. Get started by uploading company data.`}
              children={<ImportCompanyButton />}
            />}
        </div>
      </div>
    )
  }
}
const ImportCompanyButton = () => (
  <ButtonControl title='Import company data' newStyle
    class='qa-import-company-data'
    variant='brand'
    onClick={() => window.location.assign('/company/import')} />
)
export default CompanyOverview
